import React, { useState, useEffect, useCallback } from 'react';
import { useStore } from '../context/StoreContext';
import { staffAPI, extractErrorMessage } from '../api';
import { useToast } from '../components/Toast';

const DEFAULT_ROLES = ['cashier', 'manager', 'stock_clerk', 'accountant', 'owner'];

const Staff = () => {
  const { selectedStore } = useStore();
  const showToast = useToast();
  const [staffList, setStaffList] = useState([]);
  const [roles, setRoles] = useState(DEFAULT_ROLES);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showRoles, setShowRoles] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [formData, setFormData] = useState({
    email: '', full_name: '', role: 'cashier', hourly_rate: '',
  });
  const [editData, setEditData] = useState({
    role: '', is_active: true, hourly_rate: '',
  });

  const loadStaff = useCallback(async () => {
    try {
      setLoading(true);
      const response = await staffAPI.listStoreStaff(selectedStore.id);
      setStaffList(Array.isArray(response.data) ? response.data : response.data.staff_members || []);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to load staff'));
    } finally {
      setLoading(false);
    }
  }, [selectedStore]);

  const loadRoles = useCallback(async () => {
    try {
      const response = await staffAPI.getRoles();
      const d = response.data;
      setRoles(Array.isArray(d) ? d : d.roles || DEFAULT_ROLES);
    } catch (err) {
      setRoles(DEFAULT_ROLES);
    }
  }, []);

  useEffect(() => {
    if (selectedStore) {
      loadStaff();
      loadRoles();
    }
  }, [selectedStore, loadStaff, loadRoles]);

  const handleInvite = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData };
      if (data.hourly_rate) data.hourly_rate = parseInt(data.hourly_rate, 10);
      else delete data.hourly_rate;
      await staffAPI.inviteStaff(selectedStore.id, data);
      showToast('Staff member invited successfully', 'success');
      loadStaff();
      resetForm();
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to invite staff'));
    }
  };

  const handleEdit = (member) => {
    setEditData({
      role: member.role || 'cashier',
      is_active: member.is_active !== false,
      hourly_rate: member.hourly_rate || '',
    });
    setEditingStaff(member);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const data = { ...editData };
      if (data.hourly_rate) data.hourly_rate = parseInt(data.hourly_rate, 10);
      else delete data.hourly_rate;
      await staffAPI.updateStaff(selectedStore.id, editingStaff.id, data);
      showToast('Staff member updated', 'success');
      loadStaff();
      setEditingStaff(null);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to update staff'));
    }
  };

  const handleRemove = async (staffId) => {
    if (!window.confirm('Remove this staff member?')) return;
    try {
      await staffAPI.removeStaff(selectedStore.id, staffId);
      showToast('Staff member removed', 'success');
      loadStaff();
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to remove staff'));
    }
  };

  const resetForm = () => {
    setFormData({ email: '', full_name: '', role: 'cashier', hourly_rate: '' });
    setShowForm(false);
    setError(null);
  };

  if (loading) {
    return <div className="loading"><div className="spinner"></div><span>Loading staff...</span></div>;
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Staff Management</h1>
        <button className="btn btn-primary" onClick={() => { setShowForm(!showForm); if (showForm) resetForm(); }}>
          {showForm ? 'Cancel' : '+ Invite Staff'}
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Roles Reference */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-header" style={{ cursor: 'pointer' }} onClick={() => setShowRoles(!showRoles)}>
          <h3 className="card-title">Roles Reference</h3>
          <span>{showRoles ? '−' : '+'}</span>
        </div>
        {showRoles && (
          <div className="card-body">
            <div className="detail-grid">
              {(Array.isArray(roles) ? roles : DEFAULT_ROLES).map(role => {
                const roleName = typeof role === 'string' ? role : (role?.role || role?.name || role?.value || String(role));
                return (
                  <div className="detail-item" key={roleName}>
                    <div className="detail-label">{roleName}</div>
                    <div className="detail-value">{typeof role === 'object' && role?.description ? role.description : `${roleName.charAt(0).toUpperCase() + roleName.slice(1)} role`}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div className="card-header">
            <h3 className="card-title">Invite Staff Member</h3>
          </div>
          <div className="card-body">
            <form onSubmit={handleInvite}>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Email *</label>
                  <input type="email" className="form-input" value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Full Name *</label>
                  <input type="text" className="form-input" value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Role *</label>
                  <select className="form-select" value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })} required>
                    {(Array.isArray(roles) ? roles : DEFAULT_ROLES).map(r => {
                      const name = typeof r === 'string' ? r : (r?.role || r?.name || r?.value || String(r));
                      return <option key={name} value={name}>{name.charAt(0).toUpperCase() + name.slice(1)}</option>;
                    })}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Hourly Rate</label>
                  <input type="number" className="form-input" value={formData.hourly_rate} step="0.01" min="0"
                    onChange={(e) => setFormData({ ...formData, hourly_rate: e.target.value })} />
                </div>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">Send Invitation</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingStaff && (
        <div className="modal-overlay" onClick={() => setEditingStaff(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Edit Staff Member</h3>
              <button className="modal-close" onClick={() => setEditingStaff(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleUpdate}>
                <div className="form-group">
                  <label className="form-label">Role</label>
                  <select className="form-select" value={editData.role}
                    onChange={(e) => setEditData({ ...editData, role: e.target.value })}>
                    {(Array.isArray(roles) ? roles : DEFAULT_ROLES).map(r => {
                      const name = typeof r === 'string' ? r : (r?.role || r?.name || r?.value || String(r));
                      return <option key={name} value={name}>{name.charAt(0).toUpperCase() + name.slice(1)}</option>;
                    })}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Hourly Rate</label>
                  <input type="number" className="form-input" value={editData.hourly_rate} step="0.01" min="0"
                    onChange={(e) => setEditData({ ...editData, hourly_rate: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Active</label>
                  <label className="toggle-switch">
                    <input type="checkbox" checked={editData.is_active}
                      onChange={(e) => setEditData({ ...editData, is_active: e.target.checked })} />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn btn-primary">Update</button>
                  <button type="button" className="btn btn-secondary" onClick={() => setEditingStaff(null)}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Cards View */}
      <div className="mobile-cards hidden-desktop">
        {staffList.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🛡️</div>
            <h3 className="empty-state-title">No staff members</h3>
            <p>Invite your first staff member</p>
          </div>
        ) : (
          staffList.map(member => (
            <div className="mobile-card" key={member.id}>
              <div className="mobile-card-header">
                <div>
                  <div className="mobile-card-title">{member.user_full_name || member.full_name || '-'}</div>
                  <div className="text-muted" style={{ fontSize: '0.8rem' }}>{member.user_email || member.email || '-'}</div>
                </div>
                <span className={`badge ${member.is_active !== false ? 'badge-success' : 'badge-danger'}`}>
                  {member.is_active !== false ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="mobile-card-row">
                <span className="mobile-card-label">Role</span>
                <span className="badge badge-primary">{member.role}</span>
              </div>
              {member.hourly_rate && (
                <div className="mobile-card-row">
                  <span className="mobile-card-label">Rate</span>
                  <span>${parseFloat(member.hourly_rate).toFixed(2)}/hr</span>
                </div>
              )}
              <div className="mobile-card-actions">
                <button className="btn btn-sm btn-secondary" onClick={() => handleEdit(member)}>Edit</button>
                <button className="btn btn-sm btn-danger" onClick={() => handleRemove(member.id)}>Remove</button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Desktop Table View */}
      <div className="card desktop-table">
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Name</th><th>Email</th><th>Role</th><th>Rate</th><th>Status</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {staffList.map(member => (
                <tr key={member.id}>
                  <td className="font-medium">{member.user_full_name || member.full_name || '-'}</td>
                  <td className="text-muted">{member.user_email || member.email || '-'}</td>
                  <td><span className="badge badge-primary">{member.role}</span></td>
                  <td>{member.hourly_rate ? `$${parseFloat(member.hourly_rate).toFixed(2)}/hr` : '-'}</td>
                  <td>
                    <span className={`badge ${member.is_active !== false ? 'badge-success' : 'badge-danger'}`}>
                      {member.is_active !== false ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="flex gap-2">
                      <button className="btn btn-sm btn-secondary" onClick={() => handleEdit(member)}>Edit</button>
                      <button className="btn btn-sm btn-danger" onClick={() => handleRemove(member.id)}>Remove</button>
                    </div>
                  </td>
                </tr>
              ))}
              {staffList.length === 0 && (
                <tr><td colSpan="6" className="text-center text-muted" style={{ padding: '3rem' }}>No staff members found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Staff;
